<script setup lang="ts">
import CoreContextMenu from 'frontcore/views/context-menu/CoreContextMenu.vue'
import DropDownMenuSingleSelection from 'frontcore/views/drop-down/views/unit/DropDownMenuSingleSelection.vue'
import ImageIcon from 'frontcore/views/icons/image-icon/ImageIcon.vue'
import InputHelperText from "frontcore/views/select/InputHelperText.vue"
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useSelectSelection from 'frontcore/views/select/useSelectSelection'
import useSelectState from 'frontcore/views/select/useSelectState'
import { ContextMenuPosition } from 'frontcore/views/context-menu/useContextMenuPosition'
import { DropDownMenuItemProps } from 'frontcore/views/drop-down/domain/types'
import { nextTick, reactive, ref, toRefs, watch } from 'vue'
import { Nullable } from 'frontcore/presentation/types'

interface Props {
  dataTest?: string
  disabled?: boolean
  error?: string
  extraItems?: DropDownMenuItemProps[]
  iconName?: string
  items: DropDownMenuItemProps[]
  label?: string
  nullable?: boolean
  placeholder: string
  supportingText?: string
  value: Nullable<DropDownMenuItemProps['id']>
}
const props = withDefaults(defineProps<Props>(), {
  dataTest: 'select-single',
  disabled: false,
  extraItems: () => [],
  label: '',
  nullable: false,
})
const { dataTest, disabled, error, iconName, items, value, nullable, extraItems } = toRefs(props)

type Emits = (e: 'input', id: Nullable<DropDownMenuItemProps['id']>) => void
const emit = defineEmits<Emits>()

const {
  emitSearchValue,
  filteredDropDownItems,
  searchText,
  selectedSingleItem,
} = useSelectSelection(reactive({ dataTest, extraItems, iconName, items, value }))

const {
  chevronStyle,
  clickOutside,
  isFocused,
  showInput,
  showClearButton,
  stateStyleClass,
  toggleFocus,
} = useSelectState(reactive({ disabled, error, value, nullable }))

const searchInput = ref<HTMLInputElement | null>(null)
watch(
  () => isFocused.value,
  (newValue, oldValue) => {
    if (!newValue || newValue === oldValue) { return }
    nextTick(() => searchInput.value?.focus())
  }
)

const rootElement = ref<HTMLDivElement | null>(null)
const onClickOption = (id: Nullable<DropDownMenuItemProps['id']>) => {
  if (props.disabled) { return }
  selectItem(id)
  toggleFocus()
}

const onClearInput = () => {
  if (props.disabled) { return }
  selectItem(null)
  emitNativeClick()
}

const selectItem = (id: Nullable<DropDownMenuItemProps['id']>) => {
  emit('input', id)
  searchText.value = null
}

const emitNativeClick = () => {
  rootElement.value?.click()
}

const contextMenuAnchor = ref<HTMLElement | null>(null)
const contextMenuPositions: ContextMenuPosition[] = [ContextMenuPosition.bottom, ContextMenuPosition.top]
</script>

<template>
  <div
    class="input-text__container"
    :class="[stateStyleClass, {'input-text__state-disabled': disabled}]"
    :data-test="dataTest">
    <span class="input-text__label tw-inline-block">{{ label }}</span>
    <div
      ref="rootElement"
      v-click-outside="clickOutside"
      :class="{'pointer': !disabled}">
      <div
        ref="contextMenuAnchor"
        class="input-text__input-container"
        @click="toggleFocus">
        <div
          v-if="iconName || selectedSingleItem?.iconName"
          class="input-text__icon-container">
          <SVGIcon
            class="input-text__left-icon"
            :iconName="iconName || selectedSingleItem?.iconName || ''"
            :remSize="1.25" />
        </div>
        <div
          v-else-if="selectedSingleItem?.imageSrc"
          class="input-text__icon-container">
          <ImageIcon
            class="input-text__left-icon"
            :imageSrc="selectedSingleItem.imageSrc" />
        </div>
        <div
          class="tw-flex tw-align-center tw-min-w-0 tw-flex-1"
          data-test="select-single-input-container">
          <input
            v-show="showInput"
            ref="searchInput"
            class="input-text__input tw-truncate"
            data-test="select-single-input-text"
            :disabled="disabled"
            :placeholder="placeholder"
            :value="searchText ?? undefined"
            @input="emitSearchValue">
          <span
            v-show="!showInput"
            class="input-text__input tw-truncate"
            data-test="select-single-selected-value">{{ selectedSingleItem?.text }}</span>
        </div>
        <div
          v-show="error"
          class="input-text__icon-container">
          <SVGIcon
            class="input-text__right-icon fill-auxiliary-error-30"
            iconName="Error"
            :remSize="1.25" />
        </div>
        <div
          v-show="showClearButton"
          class="input-text__icon-container input-text__icon-container--small">
          <SVGIcon
            data-test="clear-button"
            iconName="Close"
            :colorClass="disabled ? 'fill-core-neutral-cold-50' : 'fill-core-neutral-cold-40'"
            :remSize="1"
            @click.stop="onClearInput" />
        </div>
        <div class="input-text__icon-container">
          <SVGIcon
            class="input-text__chevron"
            iconName="ArrowDropDown"
            :colorClass="disabled ? 'fill-core-neutral-cold-50' : 'fill-core-neutral-cold-20'"
            :remSize="1.25"
            :style="chevronStyle" />
        </div>
      </div>
      <CoreContextMenu
        v-if="isFocused"
        :anchor="contextMenuAnchor"
        :positions="contextMenuPositions"
        withAnchorWidth>
        <DropDownMenuSingleSelection
          :value="value"
          :optionList="filteredDropDownItems"
          :keepOrder="!!searchText"
          @input:value="onClickOption" />
      </CoreContextMenu>
    </div>
    <InputHelperText
      :class="{invisible: isFocused}"
      :error="error"
      :supportingText="supportingText" />
  </div>
</template>
